/* eslint-disable max-len, no-restricted-properties, padded-blocks, prefer-destructuring */

/* This mixin provides methods for calculating project totals */
import { mapState } from 'vuex';
import FiscalYear from '@/mixins/fiscalYear';

export default {
  mixins: [
    FiscalYear,
  ],
  computed: {
    ...mapState('MathHelper', {
      product: (state) => state.product,
    }),
    ...mapState('Project', {
      projectBudget: (state) => state.projectBudget,
      projectIncentivePayments: (state) => state.projectIncentivePayments,
      fundingSources: (state) => state.fundingSources,
      currentPriceLists: (state) => state.currentPriceLists,
      projectSnapshotDraft: (state) => state.projectSnapshotDraft,
    }),
    budgetSnapshotTotalCostShare() {
      if (!this.projectSnapshotDraft || this.projectSnapshotDraft === null) return 0;

      return this.projectSnapshotDraft.costShare_Base_Cost + this.projectSnapshotDraft.costShare_Contingency_Cost;
    },
    budgetTotalCostShare() {
      const budgetContingencyCostShare = this.budgetContingencyCostShare * 100;

      const budgetBaseCostShare = this.budgetBaseCostShare * 100;

      const budgetTotalCostShare = (budgetContingencyCostShare + budgetBaseCostShare) / Math.pow(10, 2);

      return budgetTotalCostShare;
    },
    budgetSnapshotBaseCostShare() {
      if (!this.projectSnapshotDraft || this.projectSnapshotDraft === null) return 0;

      return this.projectSnapshotDraft.costShare_Base_Cost;
    },
    budgetBaseCostShare() {
      let budgetBaseCostShare = 0.0;

      if (!this.projectBudget.projectBudgetYears || this.projectBudget.projectBudgetYears.length === 0) {
        return budgetBaseCostShare;
      }

      const budgetBaseCostShares = this.projectBudget.projectBudgetYears.map(
        (year) => (this.getYearCostShareSubtotal(year) * 100),
      );

      budgetBaseCostShare = budgetBaseCostShares.reduce(
        (accumulator, value) => accumulator + value,
      );

      budgetBaseCostShare /= Math.pow(10, 2);

      return budgetBaseCostShare;
    },
    budgetSnapshotContingencyCostShare() {
      if (!this.projectSnapshotDraft || this.projectSnapshotDraft === null) return 0;

      return this.projectSnapshotDraft.costShare_Contingency_Cost;
    },
    budgetContingencyCostShare() {
      let budgetContingencyCostShare = 0.0;

      if (!this.projectBudget.projectBudgetYears || this.projectBudget.projectBudgetYears.length === 0) {
        return budgetContingencyCostShare;
      }

      budgetContingencyCostShare = this.projectBudget.projectBudgetYears.map(
        (year) => (this.getTotalCostShareContingencyAmount(year)),
      ).reduce(
        (accumulator, value) => accumulator + value,
      );

      return budgetContingencyCostShare;
    },
    budgetSnapshotCostSharePercent() {
      const budgetSnapshotTotalCostShare = (this.budgetSnapshotTotalCostShare * 100);

      const budgetTotalSnapshotAmount = (this.budgetTotalSnapshotAmount * 100);

      const budgetSnapshotCostSharePercent = (budgetSnapshotTotalCostShare / (budgetTotalSnapshotAmount + budgetSnapshotTotalCostShare)) * 100;

      return budgetSnapshotCostSharePercent;
    },
    budgetCostSharePercent() {
      const budgetTotalCostShare = (this.budgetTotalCostShare * 100);

      const budgetTotalAmount = (this.budgetTotalAmount * 100);

      const budgetCostSharePercent = (budgetTotalCostShare / (budgetTotalAmount + budgetTotalCostShare)) * 100;

      return budgetCostSharePercent;
    },
    projectBudgetIncrease() {
      if (!this.projectBudget || !this.projectBudget.projectBudgetYears) {
        return 0.0;
      }
      return this.projectBudget.projectBudgetYears.flatMap((pby) => pby.annualBudgetIncreases.map(
        (ba) => ba.adjustmentToProjectBudget,
      )).reduce(
        (a, b) => a + b, 0.0,
      );
    },
    projectBudgetDecrease() {
      if (!this.projectBudget || !this.projectBudget.projectBudgetDecreases) {
        return 0.0;
      }
      return this.projectBudget.projectBudgetDecreases.map(
        (ba) => ba.decreaseAmount,
      ).reduce(
        (a, b) => a + b, 0.0,
      );
    },
    budgetTotalSnapshotAmount() {
      if (!this.projectSnapshotDraft || this.projectSnapshotDraft === null) return 0;

      const projectSnapshotTotalAmount = Math.round(this.projectSnapshotDraft.districtOrPartnerCosts_Total * 100);

      const increase = this.projectBudgetIncrease ?? 0;
      const projectBudgetIncrease = Math.round(increase * 100);

      const decrease = this.projectBudgetDecrease ?? 0;
      const projectBudgetDecrease = Math.round(decrease * 100);

      const incentiveTotal = this.projectIncentivePayments.map(
        (pmt) => (pmt.amountPaid * 100),
      ).reduce((a, b) => a + b, 0.0);

      let totalAmount = projectSnapshotTotalAmount + incentiveTotal + projectBudgetIncrease - projectBudgetDecrease;
      totalAmount /= Math.pow(10, 2);

      return totalAmount;
    },
    budgetTotalAmount() {
      if (!this.projectBudget.projectBudgetYears || this.projectBudget.projectBudgetYears.length === 0) {
        return 0.0;
      }

      const budgetTotal = this.projectBudget.projectBudgetYears.map(
        (year) => ((this.getYearTotalDistrictPartnerAmount(year)) * 100),
      ).reduce((a, b) => a + b, 0.0);

      const adjustmentTotal = this.projectBudget.projectBudgetYears.flatMap(
        (pby) => pby.annualBudgetIncreases.map(
          (pmt) => (pmt.adjustmentToProjectBudget * 100),
        ),
      ).reduce((a, b) => a + b, 0.0);

      const decreaseTotal = this.projectBudget.projectBudgetDecreases.map(
        (ba) => (ba.decreaseAmount * 100),
      ).reduce((a, b) => a + b, 0.0);

      let budgetTotalAmount = budgetTotal + adjustmentTotal - decreaseTotal;
      budgetTotalAmount /= Math.pow(10, 2);

      return budgetTotalAmount;
    },
    fundingSourceSnapshotSummaries() {
      if (!this.projectBudget || !this.projectBudget.projectBudgetYears || !this.fundingSources
        || !this.projectSnapshotDraft || !this.projectSnapshotDraft.projectBudgetYearSnapshots) {
        return [];
      }

      // compile funding sources from Line Items
      let fundingSourceMap = this.projectSnapshotDraft.projectBudgetYearSnapshots
        .flatMap((pby) => pby.projectBudgetLineItemSnapshots.map((li) => ({ ...li, pby }))).reduce(
          (entryMap, line) => {
            const fundingSourceName = this.getFundingSource(line);
            let newEntry;
            if (entryMap.has(fundingSourceName)) {
              newEntry = {
                id: line.fundingSourceId,
                name: fundingSourceName,
                contingencyCost: ((entryMap.get(fundingSourceName).contingencyCost * 100) + (this.getCostSharedContingencyOfLineItem(line.pby, line) * 100)) / Math.pow(10, 2),
                baseCost: ((entryMap.get(fundingSourceName).baseCost * 100) + (this.getCostShareOfLineItem(line) * 100)) / Math.pow(10, 2),
                adjustmentAmount: 0.0,
              };
            } else {
              newEntry = {
                id: line.fundingSourceId,
                name: fundingSourceName,
                contingencyCost: this.getCostSharedContingencyOfLineItem(line.pby, line),
                baseCost: this.getCostShareOfLineItem(line),
                adjustmentAmount: 0.0,
              };
            }
            entryMap.set(fundingSourceName, newEntry);
            return entryMap;
          },
          new Map(),
        );

      // compile funding sources from adjustments
      fundingSourceMap = this.projectBudget.projectBudgetYears
        .flatMap((pby) => pby.annualBudgetIncreases.map((adj) => ({ ...adj, pby }))).reduce(
          (entryMap, adj) => {
            const fundingSourceName = this.getFundingSource(adj);
            let newEntry;
            if (entryMap.has(fundingSourceName)) {
              newEntry = {
                id: adj.fundingSourceId,
                name: fundingSourceName,
                contingencyCost: entryMap.get(fundingSourceName).contingencyCost,
                baseCost: entryMap.get(fundingSourceName).baseCost,
                adjustmentAmount: ((entryMap.get(fundingSourceName).adjustmentAmount * 100) + (adj.adjustmentToProjectBudget * 100)) / Math.pow(10, 2),
              };
            } else {
              newEntry = {
                id: adj.fundingSourceId,
                name: fundingSourceName,
                contingencyCost: 0.0,
                baseCost: 0.0,
                adjustmentAmount: adj.adjustmentToProjectBudget,
              };
            }
            entryMap.set(fundingSourceName, newEntry);
            return entryMap;
          },
          fundingSourceMap,
        );

      // compile funding sources from decreases
      fundingSourceMap = this.projectBudget.projectBudgetDecreases.reduce(
        (entryMap, dec) => {
          const fundingSourceName = this.getFundingSource(dec);
          let newEntry;
          if (entryMap.has(fundingSourceName)) {
            newEntry = {
              id: dec.fundingSourceId,
              name: fundingSourceName,
              contingencyCost: entryMap.get(fundingSourceName).contingencyCost,
              baseCost: entryMap.get(fundingSourceName).baseCost,
              adjustmentAmount: ((entryMap.get(fundingSourceName).adjustmentAmount * 100) - (dec.decreaseAmount * 100)) / Math.pow(10, 2),
            };
          } else {
            newEntry = {
              id: dec.fundingSourceId,
              name: fundingSourceName,
              contingencyCost: 0.0,
              baseCost: 0.0,
              adjustmentAmount: dec.decreaseAmount,
            };
          }
          entryMap.set(fundingSourceName, newEntry);
          return entryMap;
        },
        fundingSourceMap,
      );

      // combine the above funding sources with the Incentives funding
      const fundingSourceSnapshotSummaries = Array.from(
        fundingSourceMap.values(),
      ).concat(
        this.fundingSourcesForIncentives,
      ).sort(
        (a, b) => a.name - b.name,
      );

      const allFundingSourceSummaries = [];
      fundingSourceSnapshotSummaries.forEach((e) => {
        if (allFundingSourceSummaries.filter((f) => f.id === e.id).length === 0) {
          allFundingSourceSummaries.push(e);
        } else {
          const total = e.baseCost + allFundingSourceSummaries.filter((f) => f.id === e.id)[0].baseCost;
          allFundingSourceSummaries.filter((f) => f.id === e.id)[0].baseCost = total;
        }
      });

      return allFundingSourceSummaries;
    },
    fundingSourceSummaries() {
      if (!this.projectBudget || !this.projectBudget.projectBudgetYears) {
        return [];
      }

      // compile funding sources from Line Items
      let fundingSourceMap = this.projectBudget.projectBudgetYears
        .flatMap((pby) => pby.projectBudgetLineItems.map((li) => ({ ...li, pby }))).reduce(
          (entryMap, line) => {
            const fundingSourceName = this.getFundingSource(line);
            let newEntry;
            if (entryMap.has(fundingSourceName)) {
              newEntry = {
                id: line.fundingSourceId,
                name: fundingSourceName,
                contingencyCost: ((entryMap.get(fundingSourceName).contingencyCost * 100) + (this.getCostSharedContingencyOfLineItem(line.pby, line) * 100)) / Math.pow(10, 2),
                baseCost: ((entryMap.get(fundingSourceName).baseCost * 100) + (this.getCostShareOfLineItem(line) * 100)) / Math.pow(10, 2),
                adjustmentAmount: 0.0,
              };
            } else {
              newEntry = {
                id: line.fundingSourceId,
                name: fundingSourceName,
                contingencyCost: this.getCostSharedContingencyOfLineItem(line.pby, line),
                baseCost: this.getCostShareOfLineItem(line),
                adjustmentAmount: 0.0,
              };
            }
            entryMap.set(fundingSourceName, newEntry);
            return entryMap;
          },
          new Map(),
        );

      // compile funding sources from adjustments
      fundingSourceMap = this.projectBudget.projectBudgetYears
        .flatMap((pby) => pby.annualBudgetIncreases.map((adj) => ({ ...adj, pby }))).reduce(
          (entryMap, adj) => {
            const fundingSourceName = this.getFundingSource(adj);
            let newEntry;
            if (entryMap.has(fundingSourceName)) {
              newEntry = {
                id: adj.fundingSourceId,
                name: fundingSourceName,
                contingencyCost: entryMap.get(fundingSourceName).contingencyCost,
                baseCost: entryMap.get(fundingSourceName).baseCost,
                adjustmentAmount: ((entryMap.get(fundingSourceName).adjustmentAmount * 100) + (adj.adjustmentToProjectBudget * 100)) / Math.pow(10, 2),
              };
            } else {
              newEntry = {
                id: adj.fundingSourceId,
                name: fundingSourceName,
                contingencyCost: 0.0,
                baseCost: 0.0,
                adjustmentAmount: adj.adjustmentToProjectBudget,
              };
            }
            entryMap.set(fundingSourceName, newEntry);
            return entryMap;
          },
          fundingSourceMap,
        );

      // compile funding sources from decreases
      fundingSourceMap = this.projectBudget.projectBudgetDecreases.reduce(
        (entryMap, dec) => {
          const fundingSourceName = this.getFundingSource(dec);
          let newEntry;
          if (entryMap.has(fundingSourceName)) {
            newEntry = {
              id: dec.fundingSourceId,
              name: fundingSourceName,
              contingencyCost: entryMap.get(fundingSourceName).contingencyCost,
              baseCost: entryMap.get(fundingSourceName).baseCost,
              adjustmentAmount: ((entryMap.get(fundingSourceName).adjustmentAmount * 100) - (dec.decreaseAmount * 100)) / Math.pow(10, 2),
            };
          } else {
            newEntry = {
              id: dec.fundingSourceId,
              name: fundingSourceName,
              contingencyCost: 0.0,
              baseCost: 0.0,
              adjustmentAmount: dec.decreaseAmount,
            };
          }
          entryMap.set(fundingSourceName, newEntry);
          return entryMap;
        },
        fundingSourceMap,
      );

      // combine the above funding sources with the Incentives funding
      const fundingSourceSummaries = Array.from(
        fundingSourceMap.values(),
      ).concat(
        this.fundingSourcesForIncentives,
      ).sort(
        (a, b) => a.name - b.name,
      );

      const allFundingSourceSummaries = [];
      fundingSourceSummaries.forEach((e) => {
        if (allFundingSourceSummaries.filter((f) => f.id === e.id).length === 0) {
          allFundingSourceSummaries.push(e);
        } else {
          const total = e.baseCost + allFundingSourceSummaries.filter((f) => f.id === e.id)[0].baseCost;
          allFundingSourceSummaries.filter((f) => f.id === e.id)[0].baseCost = total;
        }
      });

      return allFundingSourceSummaries;
    },
    fundingSourcesForIncentives() {
      if (!this.projectIncentivePayments) {
        return [];
      }

      const newMap = this.projectIncentivePayments.reduce(
        (entryMap, payment) => {
          const fundingSourceName = this.getFundingSource(payment);
          let newEntry;
          if (entryMap.has(fundingSourceName)) {
            newEntry = {
              id: payment.fundingSourceId,
              name: fundingSourceName,
              baseCost: payment.amountPaid + entryMap.get(fundingSourceName).baseCost,
              contingencyCost: 0,
            };
          } else {
            newEntry = {
              id: payment.fundingSourceId,
              name: fundingSourceName,
              baseCost: payment.amountPaid,
              contingencyCost: 0,
            };
          }
          entryMap.set(fundingSourceName, newEntry);
          return entryMap;
        },
        new Map(),
      );

      const fundingSourcesForIncentives = Array.from(newMap.values());

      return fundingSourcesForIncentives;
    },
  },
  methods: {
    async loadProjectTotalsSummaryData() {
      await this.$store.dispatch('Project/getProjectDraftSnapshot', this.project.id);
    },
    getFundingSource(item) {
      const fundingSource = this.fundingSources.find((fs) => fs.recorD_ID === item.fundingSourceId);
      if (fundingSource) {
        return fundingSource.recorD_NAME;
      }
      return '';
    },
    getYearSubtotal(budgetYear) {
      const yearSubtotal = (!budgetYear.projectBudgetLineItems || budgetYear.projectBudgetLineItems.length === 0)
        ? 0.0
        : budgetYear.projectBudgetLineItems.map((li) => (this.getTotalCost(li))).reduce(
          (accumulator, value) => accumulator + value,
        );

      const incentivePaymentsInBudgetYear = this.projectIncentivePayments.filter((incentive) => this.getFiscalYear(incentive.dateDue) === budgetYear.year);
      const incentiveTotal = (!incentivePaymentsInBudgetYear || incentivePaymentsInBudgetYear.length === 0)
        ? 0.0
        : incentivePaymentsInBudgetYear.map((incentive) => incentive.amountPaid).reduce((a, b) => a + b);

      return yearSubtotal + incentiveTotal;
    },
    getYearCostShareSubtotal(budgetYear) {
      if (!budgetYear.projectBudgetLineItems || budgetYear.projectBudgetLineItems.length === 0) {
        return 0.0;
      }

      const yearCostShareSubtotal = budgetYear.projectBudgetLineItems.map(
        (li) => this.getTotalCost(li) * (1 - (li.costShare * 0.01)),
      ).reduce(
        (accumulator, value) => accumulator + value,
      );

      return yearCostShareSubtotal;
    },
    getContingencyAmount(budgetYear) {
      if (!budgetYear.projectBudgetLineItems || budgetYear.projectBudgetLineItems.length === 0) {
        return 0.0;
      }

      const contingencyAmount = budgetYear.projectBudgetLineItems.map(
        (li) => (this.getTotalContingencyCost(li)),
      ).reduce(
        (accumulator, value) => accumulator + value,
      );

      return contingencyAmount;
    },
    getAnnualIncreaseAmount(budgetYear) {
      let totalIncrease = 0.0;
      budgetYear.annualBudgetIncreases.forEach((inc) => {
        totalIncrease += inc.adjustmentToAnnualBudget;
      });

      return totalIncrease;
    },
    getYearTotalAmount(budgetYear) {
      const yearSubtotal = (this.getYearSubtotal(budgetYear) * 100);
      const contingencyAmount = (this.getContingencyAmount(budgetYear) * 100);

      const yearTotalAmount = (yearSubtotal + contingencyAmount) / Math.pow(10, 2);

      return yearTotalAmount;
    },
    getYearTotalDistrictPartnerAmount(budgetYear) {
      const yearSubtotal = (this.getYearSubtotal(budgetYear) * 100);
      const contingencyAmount = (this.getContingencyAmount(budgetYear) * 100);
      const yearTotalAmount = (yearSubtotal + contingencyAmount) / Math.pow(10, 2);

      const yearCostShareSubtotal = (this.getYearCostShareSubtotal(budgetYear));
      const totalCostShareContingencyAmount = (this.getTotalCostShareContingencyAmount(budgetYear));
      const yearCostShareAmount = yearCostShareSubtotal + totalCostShareContingencyAmount;

      const yearDistrictPartnerTotalAmount = yearTotalAmount - yearCostShareAmount;

      return yearDistrictPartnerTotalAmount;
    },
    getCostShareOfLineItem(lineItem) {
      return this.getTotalCost(lineItem) * (lineItem.costShare * 0.01);
    },
    getCostSharedContingencyOfLineItem(year, lineItem) {
      return this.getCostShareOfLineItem(lineItem) * (lineItem.contingencyPercent * 0.01);
    },
    getFundingSourceSnapshotTotal(fundingSource) {
      const baseCost = fundingSource.baseCost;
      const contingencyCost = fundingSource.contingencyCost;
      const adjustmentAmount = fundingSource.adjustmentAmount;

      const baseAndContingency = ((baseCost * 100) + (contingencyCost * 100)) / Math.pow(10, 2);

      if (Number.isNaN(adjustmentAmount) || !adjustmentAmount) {
        return baseAndContingency;
      }

      const fundingSourceTotal = ((baseAndContingency * 100) + (adjustmentAmount * 100)) / Math.pow(10, 2);

      return fundingSourceTotal;
    },
    getFundingSourceTotal(fundingSource) {
      const baseCost = fundingSource.baseCost;
      const contingencyCost = fundingSource.contingencyCost;
      const adjustmentAmount = fundingSource.adjustmentAmount;

      const baseAndContingency = ((baseCost * 100) + (contingencyCost * 100)) / Math.pow(10, 2);

      if (Number.isNaN(adjustmentAmount) || !adjustmentAmount) {
        return baseAndContingency;
      }

      const fundingSourceTotal = ((baseAndContingency * 100) + (adjustmentAmount * 100)) / Math.pow(10, 2);

      return fundingSourceTotal;
    },
    getFundingSourceSnapshotPercentOfTotal(fundingSource) {
      let fundingSourceSnapshotTotal = this.getFundingSourceSnapshotTotal(fundingSource);
      fundingSourceSnapshotTotal *= 100;

      let budgetTotalSnapshotAmount = this.budgetTotalSnapshotAmount;
      budgetTotalSnapshotAmount *= 100;

      let fundingSourcePercentOfTotal = (fundingSourceSnapshotTotal / budgetTotalSnapshotAmount);
      fundingSourcePercentOfTotal *= 100;

      return fundingSourcePercentOfTotal;
    },
    getFundingSourcePercentOfTotal(fundingSource) {
      let fundingSourceTotal = this.getFundingSourceTotal(fundingSource);
      fundingSourceTotal *= 100;

      let budgetTotalAmount = this.budgetTotalAmount;
      budgetTotalAmount *= 100;

      let fundingSourcePercentOfTotal = (fundingSourceTotal / budgetTotalAmount);
      fundingSourcePercentOfTotal *= 100;

      return fundingSourcePercentOfTotal;
    },
    getTotalCost(item) {
      const totalCost = (item.quantity * item.unitCost);
      return totalCost;
    },
    getCostShareAmt(item) {
      const totalCost = this.getTotalCost(item);
      const costShare = item.costShare;

      const costShareAmt = (totalCost * costShare) / 100;

      return costShareAmt;
    },
    getTotalContingencyCost(item) {
      const totalContingencyCost = item.unitCost * item.quantity * (item.contingencyPercent / 100);
      return totalContingencyCost;
    },
    getTotalCostShareContingencyPercent(budgetYear) {
      if (!budgetYear.projectBudgetLineItems || budgetYear.projectBudgetLineItems.length === 0) {
        return 0.0;
      }

      const totalCostShareContingencyAmount = budgetYear.projectBudgetLineItems.map(
        (li) => this.getTotalCost(li) * (1 - (li.costShare * 0.01)) * (li.contingencyPercent * 0.01),
      ).reduce(
        (accumulator, value) => accumulator + value,
      );

      const totalCostShareAmount = budgetYear.projectBudgetLineItems.map(
        (li) => this.getTotalCost(li) * (1 - (li.costShare * 0.01)),
      ).reduce(
        (accumulator, value) => accumulator + value,
      );

      const totalCostShareContingencyPercent = totalCostShareContingencyAmount / totalCostShareAmount;

      return totalCostShareContingencyPercent;
    },
    getTotalCostShareContingencyAmount(budgetYear) {
      if (!budgetYear.projectBudgetLineItems || budgetYear.projectBudgetLineItems.length === 0) {
        return 0.0;
      }

      const totalCostShareContingencyAmount = budgetYear.projectBudgetLineItems.map(
        (li) => this.getTotalCost(li) * (1 - (li.costShare * 0.01)) * (li.contingencyPercent * 0.01),
      ).reduce(
        (accumulator, value) => accumulator + value,
      );

      return totalCostShareContingencyAmount;
    },
    getAnnualBudgetItems(budgetYear) {
      let items = [];
      const costShareSubtotal = this.getYearCostShareSubtotal(budgetYear);
      const costShareContingencyAmount = this.getTotalCostShareContingencyAmount(budgetYear);

      items = items.concat([
        {
          Costs: 'Cost Share', Base_Cost: costShareSubtotal, Contingency: costShareContingencyAmount, Total: (costShareSubtotal + costShareContingencyAmount),
        },
      ]);

      const fundingSummary = this.getBudgetYearFundingSummary(budgetYear);

      fundingSummary.forEach((fundingSource) => {
        items = items.concat([
          {
            Costs: fundingSource.name, Base_Cost: fundingSource.baseCost, Contingency: fundingSource.contingencyCost, Total: (fundingSource.baseCost + fundingSource.contingencyCost),
          },
        ]);
      });

      this.projectIncentivePayments.filter((incentive) => this.getFiscalYear(incentive.dateDue) === budgetYear.year).forEach((incentive) => {
        const fundingSource = this.fundingSources.filter((x) => x.recorD_ID === incentive.fundingSourceId)[0];

        if (items.filter((item) => item.Costs === fundingSource.recorD_NAME).length > 0) {
          items.filter((item) => item.Costs === fundingSource.recorD_NAME)[0].Base_Cost += incentive.amountPaid;
          items.filter((item) => item.Costs === fundingSource.recorD_NAME)[0].Total += incentive.amountPaid;
        } else {
          items = items.concat([
            {
              Costs: fundingSource.recorD_NAME, Base_Cost: incentive.amountPaid, Contingency: 0, Total: incentive.amountPaid,
            },
          ]);
        }
      });

      const yearSubtotal = this.getYearSubtotal(budgetYear);
      const yearContingency = this.getContingencyAmount(budgetYear);
      const yearTotal = this.getYearTotalAmount(budgetYear);

      items = items.concat([
        {
          Costs: 'Annual Cost', Base_Cost: yearSubtotal, Contingency: yearContingency, Total: yearTotal,
        },
      ]);

      return items;
    },
    getBudgetYearFundingSummary(budgetYear) {
      if (!budgetYear.projectBudgetLineItems || budgetYear.projectBudgetLineItems.length === 0) {
        return [];
      }

      // compile funding sources from Line Items
      const fundingSourceMap = this.projectBudget.projectBudgetYears.filter((yr) => yr.year === budgetYear.year)
        .flatMap((pby) => pby.projectBudgetLineItems.map((li) => ({ ...li, pby }))).reduce(
          (entryMap, line) => {
            const fundingSourceName = this.getFundingSource(line);
            let newEntry;
            if (entryMap.has(fundingSourceName)) {
              newEntry = {
                id: line.fundingSourceId,
                name: fundingSourceName,
                contingencyCost: ((entryMap.get(fundingSourceName).contingencyCost * 100) + (this.getCostSharedContingencyOfLineItem(line.pby, line) * 100)) / Math.pow(10, 2),
                baseCost: ((entryMap.get(fundingSourceName).baseCost * 100) + (this.getCostShareOfLineItem(line) * 100)) / Math.pow(10, 2),
                adjustmentAmount: 0.0,
              };
            } else {
              newEntry = {
                id: line.fundingSourceId,
                name: fundingSourceName,
                contingencyCost: this.getCostSharedContingencyOfLineItem(line.pby, line),
                baseCost: this.getCostShareOfLineItem(line),
                adjustmentAmount: 0.0,
              };
            }
            entryMap.set(fundingSourceName, newEntry);
            return entryMap;
          },
          new Map(),
        );

      const fundingSourceSnapshotSummaries = Array.from(
        fundingSourceMap.values(),
      ).sort(
        (a, b) => a.name - b.name,
      );

      return fundingSourceSnapshotSummaries;
    },
  },
};
